import { useEffect, useRef } from 'react';
import AudioTab from 'features/room/settings/tabs/AudioTab';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { type Control, type UseFormSetValue } from 'react-hook-form';
import { useTemporaryMedia } from 'hooks/useTemporaryMedia';
import { selectLocalAudioBroadcasting } from 'features/streaming/streamingSlice';
import { RoomSettingsFormValues } from 'features/room/settings/RoomSettings';
import {
  disableDeviceAutoChangeAlert,
  selectDeviceAutoChangeStatus,
} from 'features/user-media/userMediaSlice';
import { usePublishingFeed } from 'hooks/usePublishingFeed';
import { useUserAudioOutput } from 'hooks/useUserAudioOutput';
import { streamingDeviceChanged } from 'features/streaming/actions';
import { useMediaDevices } from 'hooks/useMediaDevices';
import { logger } from 'utils/logger';

export type AudioTabContainerProps = {
  control: Control<RoomSettingsFormValues>;
  setValue: UseFormSetValue<RoomSettingsFormValues>;
};

const AudioTabContainer = ({ control, setValue }: AudioTabContainerProps) => {
  const dispatch = useAppDispatch();

  const isAudioBroadcasting = useAppSelector(selectLocalAudioBroadcasting);
  const deviceAutoChange = useAppSelector(selectDeviceAutoChangeStatus);

  const { micPermissions, activeMediaDevices, persistDevices } = useMediaDevices();

  const audioOutputHandler = useUserAudioOutput();
  const { audioStream } = usePublishingFeed('media');
  const {
    requestTemporaryMedia,
    mediaStream: tempAudioStream,
    cleanupTemporaryMedia,
  } = useTemporaryMedia();

  const deviceContextRef = useRef({
    activeMediaDevices,
    isAudioBroadcasting,
  });

  deviceContextRef.current = {
    activeMediaDevices,
    isAudioBroadcasting,
  };

  useEffect(() => {
    const deviceContext = deviceContextRef.current;
    if (!deviceContext.isAudioBroadcasting && deviceContext.activeMediaDevices.audioinput) {
      requestTemporaryMedia({
        audio: {
          deviceId: {
            exact: deviceContext.activeMediaDevices.audioinput,
          },
        },
      });
    }

    return () => {
      cleanupTemporaryMedia();
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (deviceAutoChange.audiooutput) {
      setValue('audiooutput', activeMediaDevices.audiooutput || '', { shouldDirty: true });
      dispatch(disableDeviceAutoChangeAlert('audiooutput'));
    }
  }, [activeMediaDevices.audiooutput, setValue, deviceAutoChange.audiooutput, dispatch]);

  useEffect(() => {
    const requestAudio = async () => {
      if (activeMediaDevices.audioinput) {
        await requestTemporaryMedia({
          audio: {
            deviceId: {
              exact: activeMediaDevices.audioinput,
            },
          },
        });
      }
    };

    if (deviceAutoChange.audioinput) {
      setValue('audioinput', activeMediaDevices.audioinput || '', { shouldDirty: true });
      dispatch(disableDeviceAutoChangeAlert('audioinput'));

      if (!isAudioBroadcasting) {
        requestAudio();
      }
    }
  }, [
    activeMediaDevices.audioinput,
    isAudioBroadcasting,
    deviceAutoChange.audioinput,
    setValue,
    requestTemporaryMedia,
    dispatch,
  ]);

  const handleMicChange = async (deviceId: string) => {
    cleanupTemporaryMedia();

    if (!deviceId) {
      return;
    }

    persistDevices(deviceId, 'audioinput');

    if (!isAudioBroadcasting) {
      await requestTemporaryMedia({
        audio: {
          deviceId: {
            exact: deviceId,
          },
        },
      });
    }

    logger
      .remote({ action: true })
      .info(
        `User changed audioinput media device from ${activeMediaDevices.audioinput} to ${deviceId}`
      );

    dispatch(
      streamingDeviceChanged({
        id: deviceId,
        kind: 'audioinput',
      })
    );
  };

  const handleOutputChange = async (deviceId: string) => {
    persistDevices(deviceId, 'audiooutput');
    await audioOutputHandler(deviceId);
  };

  return (
    <AudioTab
      vuMeterActive
      onMicChange={handleMicChange}
      onOutputChange={handleOutputChange}
      micDisabled={micPermissions !== 'granted'}
      control={control}
      audioStream={isAudioBroadcasting ? audioStream : tempAudioStream}
      allowBroadcast
    />
  );
};

export default AudioTabContainer;
